import React, { FC, ReactElement, useContext, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import FormItemBox from '@app/components/inputs/FormItemBox';
import NakedFormInput from '@app/components/inputs/NakedFormInput';
import Button from '@app/components/Button';
import SVG from '@app/components/SVG';
import TrashIcon from '@app/images/trash-black.svg';
import PlusIcon from '@app/images/plus-primary.svg';
import { trim } from '@app/utils/stringUtils';
import FormContext from '@app/components/inputs/FormContext';

export interface AdditionalValuesField {
  keys: number[];
  isInitialValueRemoved: boolean;
  fieldName(key: number): string;
  initialValue(key: number): string | undefined;
  add(): void;
  remove(key: number): void;
  readValues(form: WrappedFormUtils): string[];
}

// Each row is its own flat form field. Nested names (`names[0]`, as DynamicListOfFormField uses) make getFieldsError
// return the list as one object, which useForm's isFormInvalid reads as an error and so disables the submit button.
export function useAdditionalValuesField(fieldNamePrefix: string, initialValues: string[] | undefined): AdditionalValuesField {
  const initialRowCount = Math.max(initialValues?.length ?? 0, 1);
  const [keys, setKeys] = useState<number[]>(() => Array.from({ length: initialRowCount }, (_, index) => index));
  // Keys are never reused, so a key below the initial row count always maps to the matching initial value
  const nextKeyRef = useRef<number>(initialRowCount);
  const [isInitialValueRemoved, setIsInitialValueRemoved] = useState<boolean>(false);

  const fieldName = (key: number): string => `${fieldNamePrefix}_${key}`;
  const initialValue = (key: number): string | undefined => initialValues?.[key];

  return {
    keys,
    isInitialValueRemoved,
    fieldName,
    initialValue,
    add: (): void => {
      const key = nextKeyRef.current++;
      setKeys((prevKeys) => [...prevKeys, key]);
    },
    remove: (key: number): void => {
      setKeys((prevKeys) => prevKeys.filter((existingKey) => existingKey !== key));

      // A removed field is no longer counted by isFieldsTouched, so dropping a stored value must be tracked here
      if (trim(initialValue(key))) {
        setIsInitialValueRemoved(true);
      }
    },
    readValues: (form: WrappedFormUtils): string[] => keys.map((key) => form.getFieldValue(fieldName(key))),
  };
}

interface Props {
  field: AdditionalValuesField;
  placeholder: string;
  addButtonText: string;
  idPrefix: string;
}

const AdditionalValuesFormItems: FC<Props> = ({ field, placeholder, addButtonText, idPrefix }) => {
  const { disabled } = useContext(FormContext);

  const renderRow = (key: number, index: number): ReactElement => (
    <Row key={key}>
      <RowFormItemBox fieldName={field.fieldName(key)} fieldDecoratorOptions={{ initialValue: field.initialValue(key) }}>
        <NakedFormInput
          name={`txt-${idPrefix}-${index}`}
          dataTestId={`txt-${idPrefix}-${index}`}
          type='text'
          placeholder={placeholder}
          disableSuggestion
        />
      </RowFormItemBox>
      <RemoveButton
        id={`btn-${idPrefix}-remove-${index}`}
        appearance='text'
        cornerType='circle'
        colorScheme='grey'
        disabled={disabled}
        onClick={(): void => field.remove(key)}
      >
        <SVG accessibilityLabel={`Remove ${placeholder}`} image={TrashIcon} width={14} height={14} />
      </RemoveButton>
    </Row>
  );

  return (
    <>
      {field.keys.map(renderRow)}
      <AddButton id={`btn-${idPrefix}-add`} appearance='text' size='small' disabled={disabled} onClick={field.add}>
        <SVG accessibilityLabel='' image={PlusIcon} width={12} height={12} />
        {addButtonText}
      </AddButton>
    </>
  );
};

export default AdditionalValuesFormItems;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`;

const RowFormItemBox = styled(FormItemBox)`
  flex: 1;
  min-width: 0;
`;

const RemoveButton = styled(Button)`
  flex: none;
  margin-top: 8px;
`;

const AddButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: -12px;
  margin-bottom: 12px;
`;
